import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { getPartners, createPartner, updatePartner, deletePartner, assignPartner, getPartnerBanksById } from '../services/api';
import { useVendorStore } from '../stores/vendorStore';
import Navbar from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Pencil, Trash2, UserCheck, Phone, Mail, MapPin, Building, Landmark, Eye, Copy } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FORM = { username: '', password: '', name: '', phone: '', email: '', partnerRegion: '', branch: '', branchCode: '', partnerNotes: '' };

const PartnersPage = () => {
  const user = useAuthStore((s) => s.user);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null);
  const [selectedBankIds, setSelectedBankIds] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [assignVendorFilter, setAssignVendorFilter] = useState('all');
  const [assignBankSearch, setAssignBankSearch] = useState('');
  const { vendors, fetchVendors } = useVendorStore();

  // Partner banks view
  const [viewPartner, setViewPartner] = useState(null);
  const [viewBanks, setViewBanks] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  // KPI detail popup
  const [kpiPopup, setKpiPopup] = useState(null); // { title, items }

  const fetchData = useCallback(async () => {
    try {
      const res = await getPartners();
      setPartners(res.data);
    } catch (err) { console.error('Failed to fetch partners:', err.message); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); fetchVendors(); }, [fetchData, fetchVendors]);

  const readyBanks = vendors.flatMap(v =>
    (v.banks || []).filter(b => b.status === 'Unassigned').map(b => ({ ...b, vendorId: v.id, vendorName: v.name }))
  );

  const handleOpenAssign = (partner, e) => {
    e.stopPropagation();
    setAssignDialog(partner);
    setSelectedBankIds([]);
    setAssignVendorFilter('all');
    setAssignBankSearch('');
  };

  const toggleBankSelection = (bankId) => {
    setSelectedBankIds(prev => prev.includes(bankId) ? prev.filter(id => id !== bankId) : [...prev, bankId]);
  };

  const handleAssignBanks = async () => {
    if (selectedBankIds.length === 0) { toast.error('Select at least one bank'); return; }
    setAssigning(true);
    let success = 0;
    for (const bankId of selectedBankIds) {
      const bank = readyBanks.find(b => b.id === bankId);
      if (!bank) continue;
      try {
        await assignPartner(bank.vendorId, bank.id, assignDialog.id);
        success++;
      } catch (error) {
        toast.error(`Failed to assign ${bank.bankName}: ${error.response?.data?.detail || 'Error'}`);
      }
    }
    if (success > 0) {
      toast.success(`${success} bank(s) assigned to ${assignDialog.name}`);
      fetchData();
      fetchVendors();
    }
    setAssigning(false);
    setAssignDialog(null);
    setSelectedBankIds([]);
  };

  const handleViewPartner = async (partner) => {
    setViewPartner(partner);
    setViewLoading(true);
    try {
      const res = await getPartnerBanksById(partner.id);
      setViewBanks(res.data.banks);
    } catch (err) {
      console.error('Failed to load branch banks:', err.message);
      setViewBanks([]);
      toast.error('Failed to load partner banks');
    }
    setViewLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Branch name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.username;
        await updatePartner(editing.id, payload);
        toast.success('Branch updated');
      } else {
        if (!form.username || !form.password) { toast.error('Username and password are required'); setSaving(false); return; }
        await createPartner(form);
        toast.success('Branch created');
      }
      setShowDialog(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
    setSaving(false);
  };

  const handleEdit = (partner, e) => {
    e.stopPropagation();
    setEditing(partner);
    setForm({
      username: partner.username || '',
      password: '',
      name: partner.name || '',
      phone: partner.phone || '',
      email: partner.email || '',
      partnerRegion: partner.partnerRegion || '',
      branch: partner.branch || '',
      branchCode: partner.branchCode || '',
      partnerNotes: partner.partnerNotes || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (partner, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete branch "${partner.name}"?`)) return;
    try {
      await deletePartner(partner.id);
      toast.success('Branch deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleCopy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied!`); }
    catch (err) { console.error('Copy failed:', err.message); toast.error('Failed to copy'); }
  };

  // KPI click handlers
  const activePartners = partners.filter(p => p.active !== false);
  const inactivePartners = partners.filter(p => p.active === false);
  const withBanks = partners.filter(p => (p.assignedBankIds?.length || 0) > 0);

  const openKpiPopup = (title, items) => setKpiPopup({ title, items });

  const filtered = partners.filter(p => {
    const s = search.toLowerCase();
    return !s || [p.name, p.username, p.phone, p.email, p.partnerRegion, p.branch].join(' ').toLowerCase().includes(s);
  });

  const getStatusBadge = (status) => {
    const styles = {
      'Active': 'bg-green-100 text-green-800',
      'Inactive': 'bg-slate-100 text-slate-600',
      'Freezed - Transaction': 'bg-red-100 text-red-800',
      'Freezed - CBI': 'bg-red-100 text-red-800',
      'Return': 'bg-orange-100 text-orange-800',
    };
    return <Badge className={styles[status] || 'bg-slate-100 text-slate-600'}>{status}</Badge>;
  };

  if (loading) {
    return (<><Navbar /><div className="flex items-center justify-center min-h-[calc(100vh-4rem)]"><div className="text-lg">Loading branches...</div></div></>);
  }

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" data-testid="partners-page-title">Branches</h1>
            <p className="text-slate-600 mt-1">Manage downline branches for bank assignment</p>
          </div>
          {user?.role === 'Admin' && (
            <Button onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowDialog(true); }} className="gap-2" data-testid="add-partner-btn">
              <Plus className="w-4 h-4" /> Add Branch
            </Button>
          )}
        </div>

        {/* Stats - All clickable */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openKpiPopup('All Branches', partners)} data-testid="kpi-total-partners">
            <div className="text-xs text-emerald-600 font-semibold">Total Branches</div>
            <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="total-partners">{partners.length}</div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openKpiPopup('Active Branches', activePartners)} data-testid="kpi-active-partners">
            <div className="text-xs text-green-600 font-semibold">Active</div>
            <div className="text-2xl font-bold text-green-700 mt-1">{activePartners.length}</div>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openKpiPopup('Inactive Branches', inactivePartners)} data-testid="kpi-inactive-partners">
            <div className="text-xs text-slate-600 font-semibold">Inactive</div>
            <div className="text-2xl font-bold text-slate-700 mt-1">{inactivePartners.length}</div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openKpiPopup('Branches With Banks', withBanks)} data-testid="kpi-with-banks">
            <div className="text-xs text-blue-600 font-semibold">With Banks</div>
            <div className="text-2xl font-bold text-blue-700 mt-1">{withBanks.length}</div>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search branches by name, phone, email, region..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="partner-search" />
        </div>

        {/* Partners Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((partner) => (
            <Card key={partner.id} className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`partner-card-${partner.id}`} onClick={() => handleViewPartner(partner)}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-emerald-700" />
                    </div>
                    <div>
                      <div className="font-semibold text-base">{partner.name}</div>
                      <div className="text-xs text-slate-500">@{partner.username}</div>
                    </div>
                  </div>
                  <Badge className={partner.active !== false ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                    {partner.active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                <div className="space-y-2 text-sm text-slate-600">
                  {partner.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" />{partner.phone}</div>}
                  {partner.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" />{partner.email}</div>}
                  {partner.partnerRegion && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400" />{partner.partnerRegion}</div>}
                  {partner.branch && <div className="flex items-center gap-2"><Building className="w-3.5 h-3.5 text-slate-400" />{partner.branch} {partner.branchCode ? `(${partner.branchCode})` : ''}</div>}
                </div>

                <div className="flex items-center justify-between mt-4 pt-3 border-t">
                  <Badge variant="outline" className="text-xs gap-1">
                    <Eye className="w-3 h-3" /> {partner.assignedBankIds?.length || 0} banks
                  </Badge>
                  {user?.role === 'Admin' && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-emerald-700 hover:bg-emerald-50 gap-1" data-testid={`assign-banks-${partner.id}`} onClick={(e) => handleOpenAssign(partner, e)}>
                        <Landmark className="w-3.5 h-3.5" />
                        <span className="text-xs">Assign</span>
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`edit-partner-${partner.id}`} onClick={(e) => handleEdit(partner, e)}>
                        <Pencil className="w-3.5 h-3.5 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" data-testid={`delete-partner-${partner.id}`} onClick={(e) => handleDelete(partner, e)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>

                {partner.partnerNotes && (
                  <div className="mt-2 text-xs text-slate-500 italic bg-slate-50 p-2 rounded">{partner.partnerNotes}</div>
                )}
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full py-12 text-center text-slate-500">
              {partners.length === 0 ? 'No branches yet. Add your first branch.' : 'No branches match your search.'}
            </div>
          )}
        </div>
      </div>

      {/* Partner Banks View Dialog */}
      <Dialog open={!!viewPartner} onOpenChange={(open) => { if (!open) setViewPartner(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-600" />
              {viewPartner?.name}'s Assigned Banks
            </DialogTitle>
          </DialogHeader>
          {viewLoading ? (
            <div className="py-8 text-center text-slate-500">Loading banks...</div>
          ) : viewBanks.length > 0 ? (
            <div className="space-y-3">
              <div className="flex gap-3 flex-wrap text-sm">
                <Badge className="bg-green-100 text-green-800">{viewBanks.filter(b => b.status === 'Active').length} Active</Badge>
                <Badge className="bg-slate-100 text-slate-600">{viewBanks.filter(b => b.status === 'Inactive').length} Inactive</Badge>
                <Badge className="bg-red-100 text-red-800">{viewBanks.filter(b => b.status?.startsWith('Freezed')).length} Freezed</Badge>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left p-2 text-xs font-semibold">Bank Name</th>
                      <th className="text-left p-2 text-xs font-semibold">Company</th>
                      <th className="text-left p-2 text-xs font-semibold">Account</th>
                      <th className="text-left p-2 text-xs font-semibold">Vendor</th>
                      <th className="text-left p-2 text-xs font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewBanks.map(bank => (
                      <tr key={bank.id} className="border-b hover:bg-slate-50">
                        <td className="p-2 font-medium">{bank.bankName}</td>
                        <td className="p-2">{bank.companyName || '-'}</td>
                        <td className="p-2">
                          <div className="flex items-center gap-1 font-mono text-xs">
                            {bank.accountNumber}
                            <button onClick={() => handleCopy(bank.accountNumber, 'Account')} className="text-slate-400 hover:text-slate-600">
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                        <td className="p-2"><Badge variant="outline" className="text-xs">{bank.vendorName}</Badge></td>
                        <td className="p-2">{getStatusBadge(bank.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-slate-500">No banks assigned to this branch.</div>
          )}
        </DialogContent>
      </Dialog>

      {/* KPI Detail Popup */}
      <Dialog open={!!kpiPopup} onOpenChange={(open) => { if (!open) setKpiPopup(null); }}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{kpiPopup?.title} ({kpiPopup?.items?.length || 0})</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {kpiPopup?.items?.map(p => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 cursor-pointer" onClick={() => { setKpiPopup(null); handleViewPartner(p); }}>
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <UserCheck className="w-4 h-4 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{p.name}</div>
                  <div className="text-xs text-slate-500">@{p.username} {p.phone ? `| ${p.phone}` : ''}</div>
                </div>
                <Badge variant="outline" className="text-xs">{p.assignedBankIds?.length || 0} banks</Badge>
                <Badge className={p.active !== false ? 'bg-green-100 text-green-800 text-xs' : 'bg-slate-100 text-slate-600 text-xs'}>
                  {p.active !== false ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            ))}
            {kpiPopup?.items?.length === 0 && <div className="py-6 text-center text-slate-400">No items</div>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Partner Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Branch' : 'Add New Branch'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {!editing && (
                <>
                  <div>
                    <Label htmlFor="p-username">Username *</Label>
                    <Input id="p-username" value={form.username} onChange={(e) => setForm(p => ({ ...p, username: e.target.value }))} required data-testid="partner-username-input" />
                  </div>
                  <div>
                    <Label htmlFor="p-password">Password *</Label>
                    <Input id="p-password" type="password" value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} required data-testid="partner-password-input" />
                  </div>
                </>
              )}
              <div className={editing ? 'col-span-2' : ''}>
                <Label htmlFor="p-name">Display Name *</Label>
                <Input id="p-name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} required data-testid="partner-name-input" />
              </div>
              {editing && (
                <div className="col-span-2">
                  <Label htmlFor="p-password-edit">New Password (leave blank to keep)</Label>
                  <Input id="p-password-edit" type="password" value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} data-testid="partner-password-input" />
                </div>
              )}
              <div>
                <Label htmlFor="p-phone">Phone</Label>
                <Input id="p-phone" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} data-testid="partner-phone-input" />
              </div>
              <div>
                <Label htmlFor="p-email">Email</Label>
                <Input id="p-email" type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} data-testid="partner-email-input" />
              </div>
              <div>
                <Label htmlFor="p-region">Region</Label>
                <Input id="p-region" value={form.partnerRegion} onChange={(e) => setForm(p => ({ ...p, partnerRegion: e.target.value }))} data-testid="partner-region-input" />
              </div>
              <div>
                <Label htmlFor="p-branch">Branch</Label>
                <Input id="p-branch" value={form.branch} onChange={(e) => setForm(p => ({ ...p, branch: e.target.value }))} data-testid="partner-branch-input" />
              </div>
              <div>
                <Label htmlFor="p-branchCode">Branch Code</Label>
                <Input id="p-branchCode" value={form.branchCode} onChange={(e) => setForm(p => ({ ...p, branchCode: e.target.value }))} data-testid="partner-branchcode-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="p-notes">Notes</Label>
              <Input id="p-notes" value={form.partnerNotes} onChange={(e) => setForm(p => ({ ...p, partnerNotes: e.target.value }))} data-testid="partner-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="partner-submit-btn">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Banks Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={(open) => { if (!open) setAssignDialog(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Banks to {assignDialog?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Select "Unassigned" banks to assign. They will automatically become "Active".</p>
          <div className="flex gap-2">
            <Select value={assignVendorFilter} onValueChange={setAssignVendorFilter}>
              <SelectTrigger className="w-48" data-testid="assign-vendor-filter"><SelectValue placeholder="Filter by vendor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors</SelectItem>
                {vendors.filter(v => (v.banks || []).some(b => b.status === 'Unassigned')).map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input placeholder="Search bank name, region..." value={assignBankSearch} onChange={(e) => setAssignBankSearch(e.target.value)} className="pl-8 h-9" data-testid="assign-bank-search" />
            </div>
          </div>
          {(() => {
            const filteredReady = readyBanks.filter(b => {
              if (assignVendorFilter !== 'all' && b.vendorId !== assignVendorFilter) return false;
              if (assignBankSearch) {
                const s = assignBankSearch.toLowerCase();
                return [b.bankName, b.region, b.accountNumber, b.vendorName].join(' ').toLowerCase().includes(s);
              }
              return true;
            });
            if (readyBanks.length === 0) {
              return (
                <div className="py-8 text-center text-slate-500">
                  <Landmark className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <p className="font-medium">No banks ready for assignment</p>
                  <p className="text-xs mt-1">Complete all 6 verifications on a bank first.</p>
                </div>
              );
            }
            return (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {filteredReady.length === 0 ? (
                  <div className="py-6 text-center text-slate-400 text-sm">No banks match your filters.</div>
                ) : filteredReady.map((bank) => (
                  <label key={bank.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedBankIds.includes(bank.id) ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`assign-bank-option-${bank.id}`}>
                    <Checkbox checked={selectedBankIds.includes(bank.id)} onCheckedChange={() => toggleBankSelection(bank.id)} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{bank.bankName}</div>
                      <div className="text-xs text-slate-500">{bank.vendorName} &middot; {bank.region} &middot; {bank.accountNumber}</div>
                    </div>
                    <Badge className="bg-teal-100 text-teal-800 text-xs">Ready</Badge>
                  </label>
                ))}
              </div>
            );
          })()}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAssignDialog(null)}>Cancel</Button>
            {readyBanks.length > 0 && (
              <Button onClick={handleAssignBanks} disabled={assigning || selectedBankIds.length === 0} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="confirm-assign-banks-btn">
                <Landmark className="w-4 h-4" />
                Assign {selectedBankIds.length > 0 ? `(${selectedBankIds.length})` : ''}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PartnersPage;
